'use client';

import * as React from 'react';
import Link from 'next/link';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

import { Button, buttonVariants } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@workspace/ui/components/card';
import { Label } from '@workspace/ui/components/label';
import {
  RadioGroup,
  RadioGroupItem
} from '@workspace/ui/components/radio-group';
import { cn } from '@workspace/ui/lib/utils';

interface EligibilityResult {
  eligible: boolean;
  message: string;
  nextSteps: string[];
}

const questions = [
  {
    id: 'accident',
    question: 'Ați fost implicat într-un accident auto?',
    options: [
      { value: 'yes', label: 'Da' },
      { value: 'no', label: 'Nu' }
    ]
  },
  {
    id: 'fault',
    question: 'Sunteți vinovatul accidentului?',
    options: [
      { value: 'yes', label: 'Da, sunt vinovat' },
      { value: 'no', label: 'Nu, nu sunt vinovat' },
      { value: 'partial', label: 'Vinovat parțial' },
      { value: 'unknown', label: 'Nu știu încă' }
    ]
  },
  {
    id: 'car_damaged',
    question: 'Mașina dumneavoastră a fost avariată și necesită reparații?',
    options: [
      { value: 'yes', label: 'Da, necesită reparații' },
      { value: 'total_loss', label: 'Da, este daună totală' },
      { value: 'no', label: 'Nu, nu are avarii' }
    ]
  },
  {
    id: 'other_rca',
    question: 'Cealaltă parte are asigurare RCA validă?',
    options: [
      { value: 'yes', label: 'Da, are RCA valid' },
      { value: 'no', label: 'Nu are RCA' },
      { value: 'unknown', label: 'Nu știu' }
    ]
  }
];

function calculateEligibility(
  answers: Record<string, string>
): EligibilityResult {
  // Not in accident = not eligible
  if (answers.accident === 'no') {
    return {
      eligible: false,
      message:
        'Serviciul nostru RCA este disponibil doar pentru victime ale accidentelor auto.',
      nextSteps: [
        'Puteți consulta serviciile noastre de închiriere de lux standard.'
      ]
    };
  }

  // Fully at fault = not eligible
  if (answers.fault === 'yes') {
    return {
      eligible: false,
      message:
        'Din păcate, dacă sunteți vinovatul accidentului, nu aveți dreptul la mașină de înlocuire decontată prin RCA.',
      nextSteps: [
        'Verificați dacă asigurarea dumneavoastră acoperă costurile de închiriere',
        'Consultați serviciile noastre de închiriere cu tarife preferențiale'
      ]
    };
  }

  // No damage = not eligible
  if (answers.car_damaged === 'no') {
    return {
      eligible: false,
      message:
        'Dacă mașina nu a fost avariată, nu este necesar un vehicul de înlocuire.',
      nextSteps: [
        'În cazul în care apar probleme ulterioare, ne puteți contacta oricând.'
      ]
    };
  }

  // No RCA from other party = complex case
  if (answers.other_rca === 'no') {
    return {
      eligible: false,
      message:
        'Dacă cealaltă parte nu are asigurare RCA, cazul este mai complex și necesită consultare juridică.',
      nextSteps: [
        'Contactați-ne pentru consultare gratuită',
        'Vă putem ajuta să vă adresați Fondului de Garantare a Asiguraților'
      ]
    };
  }

  // Partial fault or total loss = needs consultation
  if (answers.fault === 'partial' || answers.car_damaged === 'total_loss') {
    return {
      eligible: true,
      message:
        'Cazul dumneavoastră necesită evaluare individuală, dar este foarte probabil să fiți eligibil.',
      nextSteps: [
        'Contactați-ne imediat pentru consultare gratuită',
        'Pregătiți documentele: constatarea amiabilă și actele mașinii',
        'Vă vom ghida prin întregul proces legal'
      ]
    };
  }

  // Unknown fault status = needs consultation
  if (answers.fault === 'unknown' || answers.other_rca === 'unknown') {
    return {
      eligible: true,
      message:
        'Bazat pe răspunsurile dumneavoastră, este posibil să fiți eligibil. Avem nevoie de mai multe detalii.',
      nextSteps: [
        'Contactați-ne pentru o evaluare completă gratuită',
        'Pregătiți toate documentele accidentului',
        'Vă vom clarifica situația juridică'
      ]
    };
  }

  // Perfect case = fully eligible
  return {
    eligible: true,
    message:
      'Felicitări! Aveți dreptul legal la o mașină de înlocuire decontată prin RCA.',
    nextSteps: [
      'Contactați-ne imediat pentru a începe procesul',
      'Pregătiți constatarea amiabilă și documentele auto',
      'Vom livra mașina premium în maxim 24 de ore'
    ]
  };
}

export function RCAEligibilityChecker(): React.JSX.Element {
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [currentQuestion, setCurrentQuestion] = React.useState(0);
  const [result, setResult] = React.useState<EligibilityResult | null>(null);
  const [showResult, setShowResult] = React.useState(false);

  const handleAnswer = (questionId: string, value: string) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const eligibilityResult = calculateEligibility(answers);
      setResult(eligibilityResult);
      setShowResult(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleRestart = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setShowResult(false);
  };

  const canProceed = answers[questions[currentQuestion]?.id];

  if (showResult && result) {
    return (
      <Card className="mx-auto w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            {result.eligible ? (
              <CheckCircle className="h-12 w-12 text-green-500" />
            ) : (
              <XCircle className="h-12 w-12 text-red-500" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {result.eligible ? 'Sunteți eligibil!' : 'Verificare completă'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-muted-foreground text-center">{result.message}</p>

          <div>
            <h4 className="mb-3 font-semibold">Pașii următori:</h4>
            <ul className="space-y-2">
              {result.nextSteps.map((step, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2"
                >
                  <AlertTriangle className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                  <span className="text-sm">{step}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-row justify-center gap-3 pt-4">
            <Button
              onClick={handleRestart}
              variant="outline"
              className="flex-1 rounded-xl"
            >
              Verificați din nou
            </Button>
            {result.eligible && (
              <Link
                href="tel:+40799562479"
                className={cn(
                  buttonVariants({ variant: 'default' }),
                  'flex-1 rounded-xl'
                )}
              >
                Contactați-ne acum
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const question = questions[currentQuestion];

  return (
    <Card className="mx-auto w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            Verificare eligibilitate RCA
          </CardTitle>
          <span className="text-muted-foreground text-sm">
            {currentQuestion + 1} din {questions.length}
          </span>
        </div>
        <div className="bg-muted h-2 w-full rounded-full">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width: `${((currentQuestion + 1) / questions.length) * 100}%`
            }}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="mb-4 text-xl font-semibold">{question.question}</h3>
          <RadioGroup
            value={answers[question.id] || ''}
            onValueChange={(value) => handleAnswer(question.id, value)}
          >
            {question.options.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2"
              >
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                />
                <Label
                  htmlFor={option.value}
                  className="cursor-pointer"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-between pt-4">
          <Button
            onClick={handlePrevious}
            variant="outline"
            disabled={currentQuestion === 0}
          >
            Înapoi
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceed}
          >
            {currentQuestion === questions.length - 1
              ? 'Vezi rezultatul'
              : 'Următorul'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
